/**
 * Type-safe i18n translation system for tg-gallery webapp.
 *
 * All translation keys are defined in the `en` object as the source of truth.
 * Other locales must provide the same keys — TypeScript enforces completeness.
 * If a key is missing at runtime (e.g. new key added but not yet translated),
 * English is used as fallback.
 *
 * Usage: t("home.title") -> "Gallery" | "Галерея" | ...
 */

// English is the source of truth — defines all keys
const en = {
  // Home page
  "home.title": "Gallery",
  "home.my_galleries": "My Galleries",
  "home.no_galleries_yet": "No galleries yet",
  "home.send_photos_hint": "Send photos to the bot to create one!",
  "home.photos": "photos",
  "home.welcome": "Welcome to Gallery",
  "home.open_in_telegram": "Open this app in Telegram to get started.",
  "home.create_hint": "To create a gallery, send photos directly to the bot in your chat. Would you like to open the bot chat?",
  "home.invite_friends": "Invite Friends",

  // Gallery view
  "gallery.photos_count": "photos",
  "gallery.no_photos": "No photos yet",
  "gallery.share": "Share",
  "gallery.settings": "Settings",
  "gallery.edit_mode": "Edit mode",
  "gallery.send_photos_hint": "Send photos to the bot to add them to this gallery.",
  "gallery.requires_access": "This gallery requires access.",
  "gallery.unlock_for": "Unlock for",

  // Lightbox
  "lightbox.delete_photo": "Delete",
  "lightbox.download": "Download",
  "lightbox.share": "Share",
  "lightbox.add_caption": "Add a caption",
  "lightbox.create_story": "Create Story",
  "lightbox.delete_confirm": "Delete this photo?",
  "lightbox.share_text": "Check out this photo!",
  "lightbox.view_in_gallery": "View in Gallery",

  // Inbox
  "inbox.title": "Inbox",
  "inbox.expires_hint": "Photos expire in {hours}h",
  "inbox.select_all": "Select all",
  "inbox.move_to": "Move to...",
  "inbox.empty": "No photos in inbox",
  "inbox.empty_hint": "Send photos to the bot without selecting a gallery",

  // Settings
  "settings.gallery_settings": "Gallery Settings",
  "settings.title": "Title",
  "settings.description": "Description",
  "settings.visibility": "Visibility",
  "settings.layout": "Layout",
  "settings.cover_photo": "Cover Photo",
  "settings.pending_photos": "Pending Photos",
  "settings.delete_gallery": "Delete Gallery",
  "settings.public": "Public",
  "settings.unlisted": "Unlisted",
  "settings.private": "Private",
  "settings.paid": "Paid",
  "settings.price_stars": "Price (Stars)",
  "settings.approve": "Approve",
  "settings.reject": "Reject",
  "settings.auto_approve_group": "Auto-approve group uploads",
  "settings.no_pending": "No pending photos",
  "settings.add_photos_cover": "Add photos to set a cover",
  "settings.delete_confirm": "Delete this gallery? This cannot be undone.",
  "settings.type": "Type",
  "settings.columns": "Columns",
  "settings.spacing": "Spacing",
  "settings.corners": "Corners",

  // Auth
  "auth.sign_in": "Sign In",
  "auth.loading": "Loading",

  // Common
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.done": "Done",
  "common.error": "Error",
  "common.back": "Back",
  "common.loading": "Loading...",
  "common.saving": "Saving...",
} as const

/** Union type of all valid translation keys */
export type TranslationKey = keyof typeof en

/** A complete translation map — must cover every key from English */
type FullTranslation = Record<TranslationKey, string>

/** A partial translation map — missing keys fall back to English */
type PartialTranslation = Partial<FullTranslation>

// Helper to ensure full translation coverage at the type level.
// If a key is missing from a locale marked as `FullTranslation`, TS will error.
function fullTranslation(t: FullTranslation): FullTranslation { return t }
function partialTranslation(t: PartialTranslation): PartialTranslation { return t }

const ru: FullTranslation = fullTranslation({
  "home.title": "Галерея",
  "home.my_galleries": "Мои галереи",
  "home.no_galleries_yet": "Галерей пока нет",
  "home.send_photos_hint": "Отправьте фото боту, чтобы создать галерею!",
  "home.photos": "фото",
  "home.welcome": "Добро пожаловать в Галерею",
  "home.open_in_telegram": "Откройте приложение в Telegram, чтобы начать.",
  "home.create_hint": "Чтобы создать галерею, отправьте фото боту в чате. Открыть чат с ботом?",
  "home.invite_friends": "Пригласить друзей",

  "gallery.photos_count": "фото",
  "gallery.no_photos": "Фото пока нет",
  "gallery.share": "Поделиться",
  "gallery.settings": "Настройки",
  "gallery.edit_mode": "Режим редактирования",
  "gallery.send_photos_hint": "Отправьте фото боту, чтобы добавить их в эту галерею.",
  "gallery.requires_access": "Для просмотра требуется доступ.",
  "gallery.unlock_for": "Открыть за",

  "lightbox.delete_photo": "Удалить",
  "lightbox.download": "Скачать",
  "lightbox.share": "Поделиться",
  "lightbox.add_caption": "Добавить подпись",
  "lightbox.create_story": "Создать историю",
  "lightbox.delete_confirm": "Удалить это фото?",
  "lightbox.share_text": "Посмотри это фото!",
  "lightbox.view_in_gallery": "Смотреть в галерее",

  "inbox.title": "Входящие",
  "inbox.expires_hint": "Фото исчезнут через {hours}ч",
  "inbox.select_all": "Выбрать все",
  "inbox.move_to": "Переместить в...",
  "inbox.empty": "Нет фото во входящих",
  "inbox.empty_hint": "Отправьте фото боту без выбора галереи",

  "settings.gallery_settings": "Настройки галереи",
  "settings.title": "Название",
  "settings.description": "Описание",
  "settings.visibility": "Видимость",
  "settings.layout": "Макет",
  "settings.cover_photo": "Обложка",
  "settings.pending_photos": "Ожидают одобрения",
  "settings.delete_gallery": "Удалить галерею",
  "settings.public": "Публичная",
  "settings.unlisted": "По ссылке",
  "settings.private": "Приватная",
  "settings.paid": "Платная",
  "settings.price_stars": "Цена (Stars)",
  "settings.approve": "Одобрить",
  "settings.reject": "Отклонить",
  "settings.auto_approve_group": "Автоодобрение фото из группы",
  "settings.no_pending": "Нет ожидающих фото",
  "settings.add_photos_cover": "Добавьте фото, чтобы установить обложку",
  "settings.delete_confirm": "Удалить эту галерею? Это действие нельзя отменить.",
  "settings.type": "Тип",
  "settings.columns": "Колонки",
  "settings.spacing": "Отступы",
  "settings.corners": "Углы",

  "auth.sign_in": "Войти",
  "auth.loading": "Загрузка",

  "common.save": "Сохранить",
  "common.cancel": "Отмена",
  "common.done": "Готово",
  "common.error": "Ошибка",
  "common.back": "Назад",
  "common.loading": "Загрузка...",
  "common.saving": "Сохранение...",
})

const es: PartialTranslation = partialTranslation({
  "home.title": "Galería",
  "home.my_galleries": "Mis Galerías",
  "home.no_galleries_yet": "Aún no hay galerías",
  "home.send_photos_hint": "¡Envía fotos al bot para crear una!",
  "home.photos": "fotos",
  "home.welcome": "Bienvenido a Gallery",
  "home.open_in_telegram": "Abre esta app en Telegram para comenzar.",
  "home.create_hint": "Para crear una galería, envía fotos directamente al bot. ¿Quieres abrir el chat del bot?",
  "home.invite_friends": "Invitar amigos",
  "gallery.photos_count": "fotos",
  "gallery.no_photos": "Aún no hay fotos",
  "gallery.share": "Compartir",
  "gallery.settings": "Ajustes",
  "gallery.send_photos_hint": "Envía fotos al bot para añadirlas a esta galería.",
  "gallery.requires_access": "Esta galería requiere acceso.",
  "gallery.unlock_for": "Desbloquear por",
  "lightbox.delete_photo": "Eliminar",
  "lightbox.download": "Descargar",
  "lightbox.share": "Compartir",
  "lightbox.add_caption": "Añadir descripción",
  "lightbox.create_story": "Crear historia",
  "lightbox.delete_confirm": "¿Eliminar esta foto?",
  "lightbox.share_text": "¡Mira esta foto!",
  "inbox.title": "Bandeja de entrada",
  "inbox.expires_hint": "Las fotos expiran en {hours}h",
  "inbox.select_all": "Seleccionar todo",
  "inbox.move_to": "Mover a...",
  "settings.gallery_settings": "Ajustes de galería",
  "settings.title": "Título",
  "settings.description": "Descripción",
  "settings.visibility": "Visibilidad",
  "settings.delete_gallery": "Eliminar galería",
  "settings.public": "Pública",
  "settings.unlisted": "No listada",
  "settings.private": "Privada",
  "settings.paid": "De pago",
  "settings.approve": "Aprobar",
  "settings.reject": "Rechazar",
  "settings.delete_confirm": "¿Eliminar esta galería? No se puede deshacer.",
  "auth.sign_in": "Iniciar sesión",
  "common.save": "Guardar",
  "common.cancel": "Cancelar",
  "common.done": "Hecho",
  "common.error": "Error",
  "common.back": "Atrás",
  "common.loading": "Cargando...",
  "common.saving": "Guardando...",
})

const fr: PartialTranslation = partialTranslation({
  "home.title": "Galerie",
  "home.my_galleries": "Mes Galeries",
  "home.no_galleries_yet": "Pas encore de galeries",
  "home.send_photos_hint": "Envoyez des photos au bot pour en créer une !",
  "home.photos": "photos",
  "home.welcome": "Bienvenue dans Gallery",
  "home.open_in_telegram": "Ouvrez cette app dans Telegram pour commencer.",
  "home.create_hint": "Pour créer une galerie, envoyez des photos directement au bot. Voulez-vous ouvrir le chat du bot ?",
  "home.invite_friends": "Inviter des amis",
  "gallery.photos_count": "photos",
  "gallery.no_photos": "Pas encore de photos",
  "gallery.share": "Partager",
  "gallery.settings": "Paramètres",
  "gallery.send_photos_hint": "Envoyez des photos au bot pour les ajouter à cette galerie.",
  "gallery.requires_access": "Cette galerie nécessite un accès.",
  "gallery.unlock_for": "Débloquer pour",
  "lightbox.delete_photo": "Supprimer",
  "lightbox.download": "Télécharger",
  "lightbox.share": "Partager",
  "lightbox.add_caption": "Ajouter une légende",
  "lightbox.delete_confirm": "Supprimer cette photo ?",
  "lightbox.share_text": "Regarde cette photo !",
  "inbox.title": "Boîte de réception",
  "inbox.select_all": "Tout sélectionner",
  "inbox.move_to": "Déplacer vers...",
  "settings.gallery_settings": "Paramètres de la galerie",
  "settings.title": "Titre",
  "settings.description": "Description",
  "settings.delete_gallery": "Supprimer la galerie",
  "settings.approve": "Approuver",
  "settings.reject": "Rejeter",
  "auth.sign_in": "Connexion",
  "common.save": "Enregistrer",
  "common.cancel": "Annuler",
  "common.done": "Terminé",
  "common.error": "Erreur",
  "common.back": "Retour",
  "common.loading": "Chargement...",
  "common.saving": "Enregistrement...",
})

const de: PartialTranslation = partialTranslation({
  "home.title": "Galerie",
  "home.my_galleries": "Meine Galerien",
  "home.no_galleries_yet": "Noch keine Galerien",
  "home.photos": "Fotos",
  "home.welcome": "Willkommen bei Gallery",
  "gallery.photos_count": "Fotos",
  "gallery.no_photos": "Noch keine Fotos",
  "gallery.share": "Teilen",
  "gallery.settings": "Einstellungen",
  "lightbox.delete_photo": "Löschen",
  "lightbox.download": "Herunterladen",
  "lightbox.share": "Teilen",
  "lightbox.add_caption": "Beschreibung hinzufügen",
  "lightbox.delete_confirm": "Dieses Foto löschen?",
  "inbox.title": "Posteingang",
  "settings.gallery_settings": "Galerie-Einstellungen",
  "settings.title": "Titel",
  "settings.description": "Beschreibung",
  "settings.delete_gallery": "Galerie löschen",
  "settings.approve": "Genehmigen",
  "settings.reject": "Ablehnen",
  "auth.sign_in": "Anmelden",
  "common.save": "Speichern",
  "common.cancel": "Abbrechen",
  "common.done": "Fertig",
  "common.error": "Fehler",
  "common.back": "Zurück",
  "common.loading": "Laden...",
})

const pt: PartialTranslation = partialTranslation({
  "home.title": "Galeria",
  "home.my_galleries": "Minhas Galerias",
  "home.no_galleries_yet": "Nenhuma galeria ainda",
  "home.photos": "fotos",
  "home.welcome": "Bem-vindo ao Gallery",
  "gallery.photos_count": "fotos",
  "gallery.no_photos": "Nenhuma foto ainda",
  "gallery.share": "Compartilhar",
  "gallery.settings": "Configurações",
  "lightbox.delete_photo": "Excluir",
  "lightbox.download": "Baixar",
  "lightbox.delete_confirm": "Excluir esta foto?",
  "inbox.title": "Caixa de entrada",
  "settings.gallery_settings": "Configurações da galeria",
  "settings.delete_gallery": "Excluir galeria",
  "auth.sign_in": "Entrar",
  "common.save": "Salvar",
  "common.cancel": "Cancelar",
  "common.done": "Concluído",
  "common.error": "Erro",
  "common.back": "Voltar",
  "common.loading": "Carregando...",
})

const tr: PartialTranslation = partialTranslation({
  "home.title": "Galeri",
  "home.my_galleries": "Galerilerim",
  "home.no_galleries_yet": "Henüz galeri yok",
  "home.photos": "fotoğraf",
  "home.welcome": "Gallery'ye hoş geldiniz",
  "gallery.photos_count": "fotoğraf",
  "gallery.no_photos": "Henüz fotoğraf yok",
  "gallery.share": "Paylaş",
  "gallery.settings": "Ayarlar",
  "lightbox.delete_photo": "Sil",
  "lightbox.download": "İndir",
  "lightbox.delete_confirm": "Bu fotoğrafı sil?",
  "inbox.title": "Gelen kutusu",
  "settings.gallery_settings": "Galeri Ayarları",
  "settings.delete_gallery": "Galeriyi Sil",
  "auth.sign_in": "Giriş Yap",
  "common.save": "Kaydet",
  "common.cancel": "İptal",
  "common.done": "Tamam",
  "common.error": "Hata",
  "common.back": "Geri",
  "common.loading": "Yükleniyor...",
})

const uk: PartialTranslation = partialTranslation({
  "home.title": "Галерея",
  "home.my_galleries": "Мої галереї",
  "home.no_galleries_yet": "Ще немає галерей",
  "home.send_photos_hint": "Надішліть фото боту, щоб створити галерею!",
  "home.photos": "фото",
  "home.welcome": "Ласкаво просимо до Gallery",
  "home.open_in_telegram": "Відкрийте цей додаток у Telegram, щоб почати.",
  "home.invite_friends": "Запросити друзів",
  "gallery.photos_count": "фото",
  "gallery.no_photos": "Ще немає фото",
  "gallery.share": "Поділитися",
  "gallery.settings": "Налаштування",
  "gallery.send_photos_hint": "Надішліть фото боту, щоб додати їх до цієї галереї.",
  "gallery.requires_access": "Ця галерея потребує доступу.",
  "lightbox.delete_photo": "Видалити",
  "lightbox.download": "Завантажити",
  "lightbox.share": "Поділитися",
  "lightbox.add_caption": "Додати підпис",
  "lightbox.delete_confirm": "Видалити це фото?",
  "lightbox.share_text": "Подивись це фото!",
  "inbox.title": "Вхідні",
  "inbox.select_all": "Вибрати все",
  "inbox.move_to": "Перемістити в...",
  "settings.gallery_settings": "Налаштування галереї",
  "settings.title": "Назва",
  "settings.description": "Опис",
  "settings.delete_gallery": "Видалити галерею",
  "settings.approve": "Схвалити",
  "settings.reject": "Відхилити",
  "auth.sign_in": "Увійти",
  "common.save": "Зберегти",
  "common.cancel": "Скасувати",
  "common.done": "Готово",
  "common.error": "Помилка",
  "common.back": "Назад",
  "common.loading": "Завантаження...",
  "common.saving": "Збереження...",
})

const zh: PartialTranslation = partialTranslation({
  "home.title": "相册",
  "home.my_galleries": "我的相册",
  "home.no_galleries_yet": "还没有相册",
  "home.photos": "照片",
  "home.welcome": "欢迎来到 Gallery",
  "gallery.photos_count": "照片",
  "gallery.no_photos": "还没有照片",
  "gallery.share": "分享",
  "gallery.settings": "设置",
  "lightbox.delete_photo": "删除",
  "lightbox.download": "下载",
  "lightbox.share": "分享",
  "lightbox.add_caption": "添加描述",
  "lightbox.delete_confirm": "删除这张照片？",
  "inbox.title": "收件箱",
  "settings.gallery_settings": "相册设置",
  "settings.delete_gallery": "删除相册",
  "auth.sign_in": "登录",
  "common.save": "保存",
  "common.cancel": "取消",
  "common.done": "完成",
  "common.error": "错误",
  "common.back": "返回",
  "common.loading": "加载中...",
})

const ja: PartialTranslation = partialTranslation({
  "home.title": "ギャラリー",
  "home.my_galleries": "マイギャラリー",
  "home.no_galleries_yet": "ギャラリーがまだありません",
  "home.photos": "写真",
  "home.welcome": "Galleryへようこそ",
  "gallery.photos_count": "写真",
  "gallery.no_photos": "写真がまだありません",
  "gallery.share": "共有",
  "gallery.settings": "設定",
  "lightbox.delete_photo": "削除",
  "lightbox.download": "ダウンロード",
  "lightbox.delete_confirm": "この写真を削除しますか？",
  "inbox.title": "受信トレイ",
  "settings.gallery_settings": "ギャラリー設定",
  "settings.delete_gallery": "ギャラリーを削除",
  "auth.sign_in": "ログイン",
  "common.save": "保存",
  "common.cancel": "キャンセル",
  "common.done": "完了",
  "common.error": "エラー",
  "common.back": "戻る",
  "common.loading": "読み込み中...",
})

const ko: PartialTranslation = partialTranslation({
  "home.title": "갤러리",
  "home.my_galleries": "내 갤러리",
  "home.no_galleries_yet": "아직 갤러리가 없습니다",
  "home.photos": "사진",
  "home.welcome": "Gallery에 오신 것을 환영합니다",
  "gallery.photos_count": "사진",
  "gallery.no_photos": "아직 사진이 없습니다",
  "gallery.share": "공유",
  "gallery.settings": "설정",
  "lightbox.delete_photo": "삭제",
  "lightbox.download": "다운로드",
  "lightbox.delete_confirm": "이 사진을 삭제하시겠습니까?",
  "inbox.title": "받은편지함",
  "settings.gallery_settings": "갤러리 설정",
  "settings.delete_gallery": "갤러리 삭제",
  "auth.sign_in": "로그인",
  "common.save": "저장",
  "common.cancel": "취소",
  "common.done": "완료",
  "common.error": "오류",
  "common.back": "뒤로",
  "common.loading": "로딩 중...",
})

const it: PartialTranslation = partialTranslation({
  "home.title": "Galleria",
  "home.my_galleries": "Le mie gallerie",
  "home.no_galleries_yet": "Nessuna galleria ancora",
  "home.photos": "foto",
  "home.welcome": "Benvenuto in Gallery",
  "gallery.photos_count": "foto",
  "gallery.no_photos": "Nessuna foto ancora",
  "gallery.share": "Condividi",
  "gallery.settings": "Impostazioni",
  "lightbox.delete_photo": "Elimina",
  "lightbox.download": "Scarica",
  "lightbox.delete_confirm": "Eliminare questa foto?",
  "inbox.title": "In arrivo",
  "settings.gallery_settings": "Impostazioni galleria",
  "settings.delete_gallery": "Elimina galleria",
  "auth.sign_in": "Accedi",
  "common.save": "Salva",
  "common.cancel": "Annulla",
  "common.done": "Fatto",
  "common.error": "Errore",
  "common.back": "Indietro",
  "common.loading": "Caricamento...",
})

// --- Locale config ---

export type Locale = "en" | "ru" | "es" | "fr" | "de" | "pt" | "tr" | "uk" | "zh" | "ja" | "ko" | "it"

export const SUPPORTED_LOCALES: Locale[] = ["en", "ru", "es", "fr", "de", "pt", "tr", "uk", "zh", "ja", "ko", "it"]

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  ru: "Русский",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  pt: "Português",
  tr: "Türkçe",
  uk: "Українська",
  zh: "中文",
  ja: "日本語",
  ko: "한국어",
  it: "Italiano",
}

export const DEFAULT_LOCALE: Locale = "en"

export const LOCALE_STORAGE_KEY = "tg-gallery-locale"

const translations: Record<Locale, FullTranslation | PartialTranslation> = {
  en, ru, es, fr, de, pt, tr, uk, zh, ja, ko, it,
}

/**
 * Look up a translation key for the given locale.
 * Falls back to English if the key is missing in the target locale.
 */
export function translate(locale: Locale, key: TranslationKey): string {
  return (translations[locale] as Record<string, string>)?.[key] ?? en[key]
}

/**
 * Determine whether a string is a supported locale code.
 */
export function isSupportedLocale(code: string): code is Locale {
  return SUPPORTED_LOCALES.includes(code as Locale)
}
